import { Dialog } from "@headlessui/react";
import React from "react";
import Loading from "../../../common-components/Loader";
import SelectInput from "../../../common-components/CustomSelect";
import { OptionType } from "../../TMM/commonInterfaceTypes";
import { Control, UseFormWatch } from "react-hook-form";
import moment from "moment";
import {
  formatCurrency,
  formatCurrencyOne,
} from "../../../common-components/commonFunctions";
import type { PrintDetailsData } from "../modules/InventorySalesContainer";

interface PrintDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageSrc?: string;
  control: Control<any>;
  watch: UseFormWatch<any>;
  isbnNumberOptions: OptionType[];
  isbnTitleOptions: OptionType[];
  changeIsbnInfo: (event: any) => void;
  isFirefox: boolean;
  hasMoreIsbn: boolean;
  printDetails: PrintDetailsData | null;
  hasNoData: boolean;
  isLoading: boolean;
  isbnDisplay?: string | null;
}

const PrintDetailsModal: React.FC<PrintDetailsModalProps> = ({
  isOpen,
  onClose,
  imageSrc,
  control,
  watch,
  isbnNumberOptions,
  isbnTitleOptions,
  changeIsbnInfo,
  isFirefox,
  hasMoreIsbn,
  printDetails,
  hasNoData,
  isLoading,
  isbnDisplay,
}) => {
  const handleClose = () => {
    onClose();
  };

  const getDateString = (value: any): string | null => {
    if (!value) return null;
    if (typeof value === "string") return value;
    if (typeof value === "object" && value.value) return String(value.value);
    return null;
  };

  return (
    <Dialog open={isOpen} onClose={() => {}}>
      <div className="fixed inset-0 flex items-center justify-center z-50">
        <div
          className="absolute inset-0 bg-black opacity-40"
          onClick={handleClose}
        ></div>

        <div className="relative bg-white rounded-lg shadow-2xl w-[95vw] max-w-7xl mx-auto max-h-[95vh] flex flex-col">
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 text-gray-600"
            title="Close"
          >
            <span className="text-xl leading-none">&times;</span>
          </button>

          <div className="px-6 py-4 border-b border-gray-200">
            <div className="py-2 flex gap-4 items-center">
              <div className="flex items-center">
                <div className="w-[56px] h-[80px] border border-gray-200 rounded-lg overflow-hidden flex items-center justify-center bg-white">
                  {imageSrc ? (
                    <img
                      src={imageSrc}
                      alt="Book cover"
                      className="object-cover w-full h-full"
                    />
                  ) : (
                    <span className="text-xs text-gray-400 text-center px-2">
                      No Cover
                    </span>
                  )}
                </div>
              </div>

              <div className="flex-1 flex flex-col gap-4">
                <div className="flex gap-4">
                  <div className="w-[30%]">
                    <p className="text-[#e31c23] font-bold">ISBN</p>
                    <div className="w-full grid grid-cols-1">
                      <SelectInput
                        control={control}
                        name="isbn"
                        options={isbnNumberOptions}
                        placeholder="Select ISBN"
                        onChangeCallback={changeIsbnInfo}
                        watch={watch}
                        isFirefox={isFirefox}
                        hasMoreIsbn={hasMoreIsbn}
                        copyBtn={true}
                      />
                    </div>
                  </div>

                  <div className="flex-1">
                    <p className="text-[#e31c23] font-bold">BOOK TITLE</p>
                    <div className="w-full grid grid-cols-1">
                      <SelectInput
                        control={control}
                        name="title"
                        options={isbnTitleOptions}
                        placeholder="Select Book Title"
                        onChangeCallback={changeIsbnInfo}
                        watch={watch}
                        isFirefox={isFirefox}
                        hasMoreIsbn={hasMoreIsbn}
                        copyBtn={true}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-2 text-white font-semibold text-lg flex justify-between items-center">
            <span>Print Details</span>
            {isbnDisplay && (
              <span className="text-sm font-normal truncate max-w-[40%]">
                ISBN: {isbnDisplay}
              </span>
            )}
          </div>

          <Dialog.Panel className="px-6 py-4 overflow-hidden flex-1 flex flex-col">
            <Dialog.Description className="flex-1 flex flex-col">
              <Loading isLoading={isLoading}>
                <div className="border-2 text-[16px] py-10 flex flex-col relative border-gray-300 w-full">
                  {printDetails ? (
                    <div className="w-full flex justify-around">
                      <div className="flex flex-col gap-4">
                        <div className="font-bold text-gray-500">
                          Print History
                        </div>
                        <div className="font-bold text-gray-500">
                          Date :{" "}
                          {printDetails.PRINT_FILEDATE
                            ? moment(
                                printDetails.PRINT_FILEDATE
                              ).format("MM/DD/YYYY")
                            : "N/A"}
                        </div>

                        <div className="flex gap-12 justify-between items-center">
                          <span className="font-semibold text-gray-800">
                            Printing Cycle
                          </span>
                          <p className="bg-gray-200 rounded w-[150px] h-[45px] flex justify-center items-center">
                            {printDetails.PRINTING ?? "N/A"}
                          </p>
                        </div>

                        <div className="flex gap-12 justify-between items-center">
                          <span className="font-semibold text-gray-800">
                            Print WHSE Due Date
                          </span>
                          <p className="bg-gray-200 rounded w-[150px] h-[45px] flex justify-center items-center">
                            {getDateString(printDetails.PRINT_WHSE_DUE_DATE)
                              ? moment(
                                  getDateString(
                                    printDetails.PRINT_WHSE_DUE_DATE
                                  )
                                ).format("MM/DD/YYYY")
                              : "N/A"}
                          </p>
                        </div>

                        <div className="flex gap-12 justify-between items-center">
                          <span className="font-semibold text-gray-800">
                            Print QTY
                          </span>
                          <p className="bg-gray-200 rounded w-[150px] h-[45px] flex justify-center items-center">
                            {formatCurrencyOne(
                              Number(printDetails.PRINT_QTY ?? 0),
                              0,
                              ""
                            )}
                          </p>
                        </div>

                        <div className="flex gap-12 justify-between items-center">
                          <span className="font-semibold text-gray-800">
                            Total Received QTY
                          </span>
                          <p className="bg-gray-200 rounded w-[150px] h-[45px] flex justify-center items-center">
                            {formatCurrencyOne(
                              Number(printDetails.TOTAL_RECEIVED_QTY ?? 0),
                              0,
                              ""
                            )}
                          </p>
                        </div>

                        <div className="flex gap-12 justify-between items-center">
                          <span className="font-semibold text-gray-800">
                            USA Price
                          </span>
                          <p className="bg-gray-200 rounded w-[150px] h-[45px] flex justify-center items-center">
                            {formatCurrency(
                              Number(printDetails?.US_PRICE ?? 0),
                              2
                            )}
                          </p>
                        </div>

                        <div className="flex gap-12 justify-between items-center">
                          <span className="font-semibold text-gray-800">
                            Canadian Price
                          </span>
                          <p className="bg-gray-200 rounded w-[150px] h-[45px] flex justify-center items-center">
                            {formatCurrency(
                              Number(printDetails?.CANADIAN_PRICE ?? 0),
                              2
                            )}
                          </p>
                        </div>

                      </div>

                      <div className="flex flex-col gap-4">
                        <div className="font-bold text-gray-500">
                          Print Information
                        </div>
                        <div className="font-bold text-gray-500">
                          Date :{" "}
                          {printDetails.DATE
                            ? moment(printDetails.DATE).format("MM/DD/YYYY")
                            : "N/A"}
                        </div>

                        <div className="flex gap-12 justify-between items-center">
                          <span className="font-semibold text-gray-800">
                            WHSE Due Date
                          </span>
                          <p className="bg-gray-200 rounded w-[150px] h-[45px] flex justify-center items-center">
                            {getDateString(printDetails.WHSE_DUE_DATE)
                              ? moment(
                                  getDateString(printDetails.WHSE_DUE_DATE)
                                ).format("MM/DD/YYYY")
                              : "N/A"}
                          </p>
                        </div>

                        <div className="flex gap-12 justify-between items-center">
                          <span className="font-semibold text-gray-800">
                            Next Due QTY
                          </span>
                          <p className="bg-gray-200 rounded w-[150px] h-[45px] flex justify-center items-center">
                            {formatCurrencyOne(
                              Number(printDetails.NEXT_DUE_QTY ?? 0),
                              0,
                              ""
                            )}
                          </p>
                        </div>

                        <div className="flex gap-12 justify-between items-center">
                          <span className="font-semibold text-gray-800">
                           Total In Print
                          </span>
                          <p className="bg-gray-300 w-[150px] max-w-[150px] h-[45px] max-h-[45px] items-center flex justify-center rounded">
                            {formatCurrencyOne(
                              Number(printDetails.IN_PRINT ?? 0),
                              0,
                              ""
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : hasNoData ? (
                    <div className="w-full flex flex-col justify-center items-center py-8">
                      <p className="text-gray-600 font-semibold text-lg mb-2">
                        This ISBN has no data
                      </p>
                      <p className="text-gray-500 text-sm">
                        No print details available for ISBN:{" "}
                        {isbnDisplay || "N/A"}
                      </p>
                    </div>
                  ) : (
                    <div className="w-full flex justify-center items-center py-8">
                      <p className="text-gray-500">
                        No print details available
                      </p>
                    </div>
                  )}
                </div>
              </Loading>
            </Dialog.Description>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
};

export default PrintDetailsModal;


